<!-- MetricAlertsTablePatientList.vue -->
<template>
  <div data-private>
    <div v-if="!dataLoaded && !loading" class="text-center py-8">
      <button
        @click="loadPatientData"
        class="btn-primary"
      >
        <FontAwesomeIcon :icon="['fas', 'users']" />
        Load Patient Details
      </button>
      <p class="mt-3 text-sm text-gray-600">
        Click to load detailed patient information for this alert
      </p>
    </div>

    <div v-else-if="loading" class="text-center py-12">
      <FontAwesomeIcon :icon="['fas', 'spinner']" :spin="true" class="text-3xl text-primary-600 mb-3" />
      <p class="text-sm text-gray-600">Loading patient data...</p>
    </div>

    <div v-else-if="dataLoaded">
      <div v-if="rowData.length === 0" class="text-center py-12 text-gray-500">
        <FontAwesomeIcon :icon="['fas', 'user-slash']" class="text-4xl mb-3 text-gray-300" />
        <p>No patient data available for this alert</p>
      </div>

      <div v-else-if="alertInstanceId">
        <div class="mb-2 flex justify-end">
          <ExportMenu :exports="[{ type: 'pdf', action: exportToPDF }, { type: 'excel', action: exportToExcel }]" />
        </div>

        <!-- Grid Container -->
        <div class="border border-gray-200 rounded-lg overflow-hidden" style="height: 500px">
          <AgGridVue
            style="width: 100%; height: 100%"
            :theme="standardListTheme"
            :column-defs="computedColumnDefs"
            :row-data="rowData"
            :default-col-def="defaultColDef"
            :grid-options="gridOptions"
            @grid-ready="onGridReady"
          />
        </div>

        <div class="mt-2 text-sm text-gray-600">
          Showing {{ rowData.length }} patient{{ rowData.length !== 1 ? 's' : '' }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { AgGridVue } from 'ag-grid-vue3'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { isDemoMode, spoofText } from '@/utils/demoSpoof'
import { standardListTheme } from '@/utils/common'
import { generatePdf } from '@/utils/pdfExport'
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue'

export default {
  name: 'AlertPatientTable',

  components: {
    AgGridVue,
    FontAwesomeIcon,
    ExportMenu
  },
  props: {
    alertInstanceId: {
      type: Number,
      required: true
    },
    tenantId: {
      type: Number,
      default: 2
    }
  },

  data() {
    return {
      loading: false,
      dataLoaded: false,
      gridApi: null,
      rowData: [],
      detailColumns: [],
      gridOptions: {
        suppressAggFuncInHeader: true,
        domLayout: 'normal'
      },
      defaultColDef: {
        sortable: true,
        filter: true,
        resizable: true,
        minWidth: 100
      }
    }
  },

  computed: {
    standardListTheme() {
      return standardListTheme
    },

    computedColumnDefs() {
      // Mandatory columns that are always shown
      const mandatoryColumns = [
        {
          headerName: 'Patient ID',
          field: 'bene_mbi_id',
          maxWidth: 150,
        },
        {
          headerName: 'Contract',
          field: 'contract_id',
          maxWidth: 120
        },
        {
          headerName: 'Observation Date',
          field: 'observation_date',
          maxWidth: 130,
          valueFormatter: params => {
            if (!params.value) return ''
            return new Date(params.value).toLocaleDateString()
          }
        }
      ]

      // Add custom columns from detail_query_config
      const customColumns = this.detailColumns.map(colName => {
        // Convert column name to header name
        const headerName = colName
          .toLowerCase()
          .replace(/_/g, ' ')
          .replace(/\b\w/g, char => char.toUpperCase())

        return {
          headerName: headerName,
          field: colName.toLowerCase(),
          cellClass: 'cell-theme-left-aligned',
          // ag-grid tooltips render in a detached popup the DOM spoof engine never
          // sees. These patient-detail columns can carry PHI, so spoof in Demo Mode.
          tooltipValueGetter: params => {
            const val = params.value
            if (val == null || val === '') return val
            return isDemoMode() ? spoofText(String(val)) : val
          },
          minWidth: 120,
          // Special formatting for certain column types
          valueFormatter: params => {
            if (!params.value) return '—'

            // Format dates
            if (colName.includes('DATE') || colName.includes('DT')) {
              try {
                return new Date(params.value).toLocaleDateString()
              } catch (e) {
                return params.value
              }
            }

            // Format numbers
            if (typeof params.value === 'number') {
              if (colName.includes('AMOUNT') || colName.includes('COST')) {
                return `$${params.value.toFixed(2)}`
              }
              if (colName.includes('PERCENT') || colName.includes('RATE')) {
                return `${(params.value * 100).toFixed(1)}%`
              }
              return params.value.toFixed(2)
            }

            return params.value
          }
        }
      })

      return [...mandatoryColumns, ...customColumns]
    }
  },

  methods: {
    async loadPatientData() {
      this.loading = true
      try {
        const response = await axios.get(`/api/impact_analytics/composable-alert-instances/${this.alertInstanceId}/patient-details/`)

        if (response.data.error) {
          console.error('Error from API:', response.data.error)
          this.rowData = []
        } else if (response.data.patient_details && Array.isArray(response.data.patient_details)) {
          this.rowData = response.data.patient_details
          // Update detail columns if provided in the response
          if (response.data.detail_columns && Array.isArray(response.data.detail_columns)) {
            this.detailColumns = response.data.detail_columns
          }
        } else if (Array.isArray(response.data)) {
          // Fallback for direct array response
          this.rowData = response.data
        } else {
          this.rowData = []
        }

        this.dataLoaded = true
      } catch (error) {
        console.error('Failed to load patient details:', error)
        this.rowData = []
        this.dataLoaded = true
      } finally {
        this.loading = false
      }
    },

    onGridReady(params) {
      this.gridApi = params.api
      params.api.sizeColumnsToFit()

      // Set up resize listener
      window.addEventListener('resize', this.onWindowResize)
    },

    onWindowResize() {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit()
      }
    },

    exportToExcel() {
      if (this.gridApi) {
        if (this.$root?.isDemoMode) return; // Demo Mode: block raw-data export
        this.gridApi.exportDataAsExcel({
          fileName: `alert_patients_${this.alertInstanceId}.xlsx`,
          allColumns: true
        })
      }
    },

    exportToPDF() {
      if (!this.gridApi) return

      // Get visible columns
      const visibleColumns = this.computedColumnDefs.map(col => ({
        header: col.headerName,
        dataKey: col.field,
        width: col.maxWidth || 80
      }))

      // Get the data
      const dataToExport = []
      this.gridApi.forEachNodeAfterFilterAndSort(node => {
        if (node.data) {
          dataToExport.push({...node.data})
        }
      })

      // Generate PDF
      generatePdf({
        rowData: dataToExport,
        columns: visibleColumns,
        title: `Alert Patient Details - ID ${this.alertInstanceId}`,
        orientation: 'l' // Landscape for better column fit
      })
    }
  },
  mounted() {
    console.log(this.alertInstanceId)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.onWindowResize)
  }
}
</script>

<style scoped>

</style>
